/** @module components/choice-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View, {ViewRegistrationMap} from '@lernetz/ts-lib/src/core/view/View';
import Repeat from '@lernetz/ts-lib/src/components/Repeat';

import {Choice} from './Choice';
import {TextChoice, TextChoiceView} from './TextChoice';

import {Quiz, QuizInputState, QuizVisitor} from '../quiz';

import {ChoiceQuiz as template} from '../../templates/choice-quiz';


/**
 * Specifies the type of a [[ChoiceQuiz]] (either `'single'` or `'multiple'`).
 */
export type ChoiceQuizType = 'multiple' | 'single';


/**
 * [[QuizVisitor]]s that support [[ChoiceQuiz]] should implement this interface.
 */
export interface ChoiceQuizVisitor<TResult = void> extends QuizVisitor<TResult> {
    visitChoiceQuiz(quiz: ChoiceQuiz): TResult;
}


function isChoiceQuizVisitor<TResult>(visitor: QuizVisitor<TResult>): visitor is ChoiceQuizVisitor<TResult> {
    return typeof visitor['visitChoiceQuiz'] !== 'undefined';
}


/**
 * Specifies the initial state of a [[ChoiceQuiz]].
 */
export interface ChoiceQuizInitialState {

    /**
     * Sets the available choices for the single-choice quiz.
     */
    choices: Choice[];

    /**
     * Sets the type of choice quiz (`'single'` or `'multiple'`).
     */
    type: ChoiceQuizType;
}


/**
 * A type of [[Quiz]] that models single-choice and multiple-choice quizzes.
 *
 * @example
 *```typescript
 *
 * import {ChoiceQuiz, TextChoice, registrations as choiceQuizRegistrations} from '@lernetz/lernfragen/components/choice-quiz';
 *
 * const singleChoiceQuiz = new ChoiceQuiz({
 *     type: 'single',
 *     choices: [
 *         new TextChoice({ text: 'I am the only correct answer', correct: true }),
 *         new TextChoice({ text: 'I am a wrong answer', correct: false }),
 *         new TextChoice({ text: 'I am another wrong answer', correct: false }),
 *     ],
 * });
 *```
 *
 * Don't forget to register `choiceQuizRegistrations`. Then, in your HTML template:
 *
 *```html
 * <div view="[[singleChoiceQuiz]]" data="[[singleChoiceQuiz]]"></div>
 *```
 *
 * @seealso [[Quiz]], the main interface implemented by this class.
 */
export class ChoiceQuiz extends Model implements Quiz {

    /**
     * Initializes a new instance of the [[ChoiceQuiz]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: ChoiceQuizInitialState, data: object = {}) {
        super({
            choices: [],
            inputState: 'none',
            type: 'multiple',
            ...initialState,
            ...data
        });

        this.updateInputState();
        for (const ch of this.choices) {
            ch.change.filter(c => c.name === 'selected').subscribe(_ => this.updateInputState());
        }
    }

    /**
     * Gets all choices in the quiz.
     */
    public readonly choices: Choice[];

    /** @inheritdoc */
    public readonly inputState: QuizInputState;

    /**
     * Gets the type of this choice quiz (either `'single'` or `'multiple'`).
     */
    public readonly type: ChoiceQuizType;

    /**
     * Dispatches to [[ChoiceQuizVisitor.visitChoiceQuiz]] (if implemented by `visitor`).
     */
    public accept<TResult = void>(visitor: QuizVisitor<TResult>): TResult {
        if (isChoiceQuizVisitor(visitor)) {
            return visitor.visitChoiceQuiz(this);
        }
    }

    /**
     * Callback method that gets invoked when the user clicks or taps on the specified `choice`.
     * It sets that choice's [[Choice.selected]] state to `true`, and resets all other choices' `selected` state.
     *
     * @param choice The [[Choice]] that was clicked or tapped by the user.
     */
    public onChoiceClicked(choice: Choice): void {
        switch (this.type) {

            case 'multiple':
                choice.selected = !choice.selected;
                break;

            case 'single':
                for (const ch of this.choices) {
                    ch.selected = (ch === choice);
                }
                break;
        }
    }

    /**
     * Updates the [[inputState]] property.
     */
    protected updateInputState(): void {
        let inputState: QuizInputState;
        if (this.choices.some(ch => ch.selected)) {
            inputState = this.choices.some(ch => ch.selected !== ch.correct) ? 'incorrect' : 'correct';
        }
        else {
            inputState = 'none';
        }
        this.set('inputState', inputState);
    }
}


/**
 * Base `View` class to render a [[ChoiceQuiz]].
 * Subclass this type if you want to use a different template.
 */
export class ChoiceQuizView extends View<ChoiceQuiz> {

    /**
     * Initializes a new instance of the [[ChoiceViewQuiz]] class.
     */
    public constructor(type: ChoiceQuizType) {
        super({
            template,
            registrations: ChoiceQuizView.getRegistrations(type),
        })
    }

    private static getRegistrations(type: ChoiceQuizType): ViewRegistrationMap {
        const registrations = new ViewRegistrationMap();
        registrations.set('repeat', Repeat);
        registrations.set(TextChoice, () => new TextChoiceView(type));
        return registrations;
    }
}


/**
 * A set of view registrations for [[ChoiceQuiz]].
 */
export const registrations = new ViewRegistrationMap();
registrations.set(ChoiceQuiz, (choiceQuiz: ChoiceQuiz) => new ChoiceQuizView(choiceQuiz.type));
